'use client';

import { useRouterState } from '@tanstack/react-router';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const PROGRESS_COLOR = '#fc4c53';
const NAV_START = 0.16;
const NAV_MAX = 0.68;
const FINISH_TARGET = 2;
const SHOW_DELAY_MS = 50;
const FINISH_HOLD_MS = 80;
const FADE_OUT_MS = 500;
const TICK_BASE_INTERVAL_MS = 260;
const TICK_JITTER_MS = 220;
const TRICKLE_RATIO = 0.06;
const SPRING_LOAD = { type: 'spring' as const, stiffness: 80, damping: 26, mass: 1 }; // Critically over-damped to mimic Svelte Spring(stiffness: 0.1, damping: 1) — slow, no oscillation.
const SPRING_FINISH = { type: 'spring' as const, stiffness: 220, damping: 28, mass: 1 }; // Punchy snap toward FINISH_TARGET so the slide-off has weight even if the navigation finished while the bar was still low.

export default function NavigationProgress() {
  const isLoading = useRouterState({ select: (state) => state.isLoading || state.isTransitioning }); // `isLoading` covers any in-flight loader; `isTransitioning` covers the React commit that follows. Together they fence the perceived navigation window.
  const progress = useMotionValue(0);
  const visualScaleX = useTransform(progress, (value) => Math.min(value, 1)); // Spring overshoots past 1 toward FINISH_TARGET so the bar slides off with momentum; clamp the rendered transform at 100% so the bar visually parks at full width before fading.
  const [opaque, setOpaque] = useState(false);
  const visibleRef = useRef(false);
  const showedCurrentLoadRef = useRef(false);
  const loadStartedAtRef = useRef<number | null>(null);
  const cycleRef = useRef(0);

  useEffect(() => {
    const unsubscribe = progress.on('change', (value) => setOpaque(visibleRef.current && value < 1)); // Fade the bar the instant the visible head hits 100%; the spring keeps coasting toward FINISH_TARGET behind a faded layer so rapid back-to-back navigations feel weighted rather than chopped.
    return unsubscribe;
  }, [progress]);

  useEffect(() => {
    const updateOpaque = (next: boolean) => {
      visibleRef.current = next;
      setOpaque(next && progress.get() < 1);
    };
    const currentCycle = ++cycleRef.current;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let startDelayTimer: ReturnType<typeof setTimeout> | null = null;
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
    let currentAnim: ReturnType<typeof animate> | null = null;

    const cleanup = () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (timer !== null) clearTimeout(timer);
      if (startDelayTimer !== null) clearTimeout(startDelayTimer);
      if (fadeTimer !== null) clearTimeout(fadeTimer);
      currentAnim?.stop();
    };

    const showProgress = () => {
      // Only restart from 0 if this is a brand-new cycle, the bar isn't already showing, or the previous run already crossed the finish line. A new nav while we're still mid-trickle keeps climbing instead of yanking back to 0.
      const shouldAnimateFromZero = !showedCurrentLoadRef.current || !visibleRef.current || progress.get() >= 1;
      updateOpaque(true);
      showedCurrentLoadRef.current = true;
      if (!shouldAnimateFromZero) return;
      progress.set(0);
      rafId = requestAnimationFrame(() => {
        if (disposed || cycleRef.current !== currentCycle) return;
        currentAnim = animate(progress, NAV_START, SPRING_LOAD);
      });
    };

    const startTicker = () => {
      const tick = () => {
        timer = setTimeout(
          () => {
            if (disposed || cycleRef.current !== currentCycle) return;
            const current = progress.get();
            const headroom = NAV_MAX - current;
            if (headroom > 0.002) {
              currentAnim?.stop();
              currentAnim = animate(progress, current + headroom * TRICKLE_RATIO, SPRING_LOAD);
            }
            tick();
          },
          TICK_BASE_INTERVAL_MS + Math.random() * TICK_JITTER_MS,
        );
      };
      tick();
    };

    if (isLoading) {
      loadStartedAtRef.current ??= performance.now();
      const remainingDelay = Math.max(0, SHOW_DELAY_MS - (performance.now() - loadStartedAtRef.current));
      const startLoadingProgress = () => {
        showProgress();
        startTicker();
      };
      if (remainingDelay > 0) {
        startDelayTimer = setTimeout(() => {
          if (disposed || cycleRef.current !== currentCycle) return;
          startLoadingProgress();
        }, remainingDelay);
      } else startLoadingProgress();
      return cleanup;
    }

    if (!showedCurrentLoadRef.current) {
      loadStartedAtRef.current = null;
      return cleanup;
    }
    loadStartedAtRef.current = null;
    updateOpaque(true); // Stay mounted while the finish spring runs; the change subscription will flip opaque off once visualScaleX crosses 1.
    currentAnim = animate(progress, FINISH_TARGET, SPRING_FINISH);
    fadeTimer = setTimeout(() => {
      if (disposed || cycleRef.current !== currentCycle) return;
      updateOpaque(false);
      progress.set(0);
      showedCurrentLoadRef.current = false;
      loadStartedAtRef.current = null;
    }, FADE_OUT_MS + FINISH_HOLD_MS);

    return cleanup;
  }, [isLoading, progress]);

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-x-0 top-0 z-[400] h-[3px]">
      <motion.div
        className="h-full origin-left"
        style={{
          backgroundColor: PROGRESS_COLOR,
          scaleX: visualScaleX,
          opacity: opaque ? 1 : 0,
          transition: opaque ? 'opacity 0ms' : `opacity ${FADE_OUT_MS}ms`,
        }}
      />
    </div>
  );
}
